import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { AsyncState } from 'shared/utils/async-state';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDEmptyState from 'ud-ui/components/empty-state';
import UDPagedListSkeleton from 'ud-ui/components/paged-list/components/paged-list-skeleton';
import UDPagedListToolbar from 'ud-ui/components/paged-list/components/paged-list-toolbar';

const DEFAULT_SKELETON_COUNT = 6;

export interface UDEntityListProps<T> {
  /** Все элементы списка: резать их некому — пагинации здесь нет. */
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Ключ строки. По умолчанию — позиция в списке. */
  getItemKey?: (item: T, index: number) => React.Key;

  /** Какую ветку рисовать; `isRefreshing` притушивает уже показанное. */
  state: AsyncState;

  /** Значение поля поиска. Фильтрует список сам вызывающий. */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Дополнительные фильтры, рисуются в тулбаре слева, рядом с поиском. */
  extraFilters?: React.ReactNode;
  /** Кнопки действий над списком, рисуются в тулбаре справа. */
  actions?: React.ReactNode;

  /** Текст, когда список пуст сам по себе. */
  emptyText?: React.ReactNode;
  emptySubtitle?: React.ReactNode;
  /** Кнопка под текстом пустого состояния — обычно «Добавить …». */
  emptyAction?: React.ReactNode;
  /** Текст, когда пуст результат поиска. */
  notFoundText?: React.ReactNode;
  errorText?: React.ReactNode;

  className?: string;
  listClassName?: string;
  /**
   * Раскладка самих элементов. По умолчанию — колонка строк; экран с карточками
   * передаёт свою сетку. Значение **заменяет** дефолт, а не дописывается к нему:
   * `display` от двух классов сразу не складывается.
   */
  itemsClassName?: string;
  /** Высота одной заглушки скелетона: у строки и у карточки она разная. */
  skeletonRowHeight?: number;
  /** Сколько заглушек рисовать на первой загрузке. */
  skeletonCount?: number;

  /**
   * Размер страницы. Списку внутри карточки он не нужен — проп существует ради
   * `UDPagedList`, который строится поверх этого компонента и добавляет
   * «На странице» в тот же тулбар.
   */
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  /** Подвал под списком — у `UDPagedList` там пагинатор. */
  footer?: React.ReactNode;
}

/**
 * Список сущностей: тулбар с поиском, ветки состояний, строки.
 *
 * Ядро всех списков приложения. Список внутри карточки (состав команды, участники,
 * команды игрока) берёт его напрямую: данные таких вкладок приходят целиком одним
 * запросом, страниц у них нет. Список-страница берёт `UDPagedList`, который
 * дописывает сюда «На странице» и пагинатор — так две разновидности физически не
 * могут разъехаться по виду.
 *
 * Компонент **чисто презентационный**: он не знает ни про URL, ни про RTK Query,
 * ни про роуты — всё приходит пропсами (правило «`ud-ui` не импортирует фичи» из
 * `.claude/rules/architecture.md`).
 *
 * Ветку рендера выбирает только `state.status`; «идёт запрос» само по себе список
 * не стирает — скелетон показывается лишь на первой загрузке
 * (`.claude/docs/async-loading-states.md`).
 */
function UDEntityList<T>({
  items,
  renderItem,
  getItemKey,
  state,
  search,
  onSearchChange,
  searchPlaceholder,
  extraFilters,
  actions,
  emptyText: emptyTextProp,
  emptySubtitle,
  emptyAction,
  notFoundText: notFoundTextProp,
  errorText: errorTextProp,
  className,
  listClassName,
  itemsClassName = 'flex flex-col gap-2.5',
  skeletonRowHeight,
  skeletonCount,
  perPage,
  onPerPageChange,
  footer,
}: UDEntityListProps<T>) {
  const { t } = useLingui();
  const emptyText = emptyTextProp ?? t`Список пуст`;
  const notFoundText = notFoundTextProp ?? t`Ничего не найдено`;
  const errorText = errorTextProp ?? t`Не удалось загрузить список`;
  const isSearching = search.trim().length > 0;

  const empty = useMemo(
    () =>
      isSearching ? (
        // При поиске кнопка создания не предлагается: пусто не потому, что
        // ничего нет, а потому, что не совпало с запросом.
        <UDEmptyState title={notFoundText} />
      ) : (
        <UDEmptyState title={emptyText} subtitle={emptySubtitle} action={emptyAction} />
      ),
    [isSearching, notFoundText, emptyText, emptySubtitle, emptyAction],
  );

  return (
    <div className={classNames('flex h-full flex-col', className)}>
      <UDPagedListToolbar
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        // Ветка `loading` при непустом поиске и есть «запрос в пути»; когда список
        // уже на экране, о том же говорит `isRefreshing`.
        isSearchLoading={state.isRefreshing || state.status === 'loading'}
        perPage={perPage}
        onPerPageChange={onPerPageChange}
        extraFilters={extraFilters}
        actions={actions}
      />

      <UDAsyncContent
        state={state}
        data-testid="paged-list-content"
        className={classNames('flex-1', listClassName)}
        loading={
          <UDPagedListSkeleton
            count={skeletonCount ?? perPage ?? DEFAULT_SKELETON_COUNT}
            className={itemsClassName}
            rowHeight={skeletonRowHeight}
          />
        }
        empty={empty}
        error={<UDEmptyState title={errorText} />}
      >
        <div className={itemsClassName} role="list">
          {items.map((item, index) => (
            <React.Fragment key={getItemKey ? getItemKey(item, index) : index}>{renderItem(item, index)}</React.Fragment>
          ))}
        </div>
      </UDAsyncContent>

      {footer}
    </div>
  );
}

export default UDEntityList;
